<template>
  <div class="qa-qc-main well-page">

    <TabularLoaders v-if="showLoader" />
    
    <!-- Main Data Table with Color Indicators -->
    <div v-if="data.length > 0" class="qaqc-main-container">
      <!-- Color Indicators at Top -->
      <div class="qaqc-top-indicators">
        <div class="qaqc-indicator-item">
          <div class="qaqc-indicator-dot passed"></div>
          <span>Passed</span>
        </div>
        <div class="qaqc-indicator-item">
          <div class="qaqc-indicator-dot failed"></div>
          <span>Failed</span>
        </div>
        <div class="qaqc-indicator-item">
          <div class="qaqc-indicator-dot pending"></div>
          <span>Pending</span>
        </div>
      </div>
      
      <!-- Table Section -->
      <div class="qaqc-table-section">
        <v-table class="qaqc-main-table">
      <thead>
             <tr class="qaqc-table-header">
               <th class="qaqc-main-header">{{ columnDisplayNames?.fluidSampleID || 'Fluid Sample ID' }}</th>
               <th class="qaqc-main-header">Tests</th>
               <th class="qaqc-main-header">{{ columnDisplayNames?.field || 'Field' }}</th>
               <th class="qaqc-main-header">{{ columnDisplayNames?.reservoir || 'Reservoir' }}</th>
               <th class="qaqc-main-header">{{ columnDisplayNames?.fluidType || 'Fluid Type' }}</th>
               <th class="qaqc-main-header">{{ columnDisplayNames?.fluidClassification || 'Fluid Classification' }}</th>
               <th class="qaqc-main-header">{{ columnDisplayNames?.reservoirPressure || 'Reservoir Pressure' }}</th>
               <th class="qaqc-main-header">{{ columnDisplayNames?.reservoirTemperature || 'Reservoir Temperature' }}</th>
        </tr>
      </thead>
      <tbody class="well-table-content">
        <template v-for="(item, index) in data" :key="index">
              <tr class="qaqc-table-row">
            <td>{{ item.fluidSampleID }}</td>
                         <td>
                  <div class="qaqc-tests-container">
                 <span 
                   v-for="(test, testIndex) in item.testsArray" 
                   :key="testIndex"
                      :class="getTestStatusClass(test, item)"
                   @click="handleTestClick(test, item)"
                 >
                   {{ test.displayName }}
                 </span>
               </div>
             </td>
            <td>{{ item.field }}</td>
            <td>{{ item.reservoir }}</td>
            <td>{{ item.fluidType }}</td>
            <td>{{ item.fluidClassification }}</td>
            <td>{{ formatValueWithUOM(item.reservoirPressure, 'reservoirPressure', item.originalSample) }}</td>
            <td>{{ formatValueWithUOM(item.reservoirTemperature, 'reservoirTemperature', item.originalSample) }}</td>
          </tr>
        </template>
      </tbody>
    </v-table>
       </div>
     </div>


    <!-- Pagination -->
    <div class="qaqc-pagination-container" v-if="totalItems > 0">
      <div class="qaqc-pagination-left">
        <v-select
          v-model="itemsPerPage"
          :items="[10, 20, 50, 100]"
          label="Items per page"
          variant="outlined"
          density="compact"
          class="items-per-page-select"
          @update:model-value="onItemsPerPageChange"
        ></v-select>
      </div>
      
      <div class="qaqc-pagination-center">
        <span class="qaqc-pagination-info">
           {{ startIndex + 1 }}-{{ endIndex }} of {{ totalItems }} Samples
         </span>
       </div>
      
      <div class="qaqc-pagination-right">
        <v-btn
          icon="mdi-chevron-left"
          variant="text"
          :disabled="currentPage === 1"
          @click="previousPage"
          class="qaqc-pagination-btn"
        ></v-btn>
        <v-btn
          icon="mdi-chevron-right"
          variant="text"
          :disabled="currentPage >= totalPages"
          @click="nextPage"
          class="qaqc-pagination-btn"
        ></v-btn>
      </div>
    </div>

    <!-- No Data Messages -->
    <div v-if="data.length === 0 && !showLoader && timeoutMessage === ''" class="no-asset-div">
      <div class="text-center">
        <v-icon color="grey" size="large" class="mb-2">mdi-database-off</v-icon>
        <h4>No data found</h4>
        <p>There is no data available for QA/QC review at this time.</p>
      </div>
    </div>

    <div v-else-if="timeoutMessage !== ''" class="no-asset-div timeout">
      <div class="text-center">
        <v-icon color="warning" size="large" class="mb-2">mdi-alert-circle</v-icon>
        <p>{{ timeoutMessage }}</p>
        <v-btn color="primary" @click="fetchData()" prepend-icon="mdi-reload">
          Retry
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import api from "../../axiosInterceptor";
import TabularLoaders from "../../components/layout/Loaders/TabularLoaders.vue";
import { getDisplayName } from "../../utils/cache.js";

export default {
  name: "QAQCDetailView",
  components: {
    TabularLoaders,
  },
  data() {
    return {
      activeTab: 'Tests',
      data: [],
      tenantId: localStorage.getItem("tenantId"),
      showLoader: false,
      timeoutMessage: "",
      currentPage: 1,
      itemsPerPage: 20,
      totalItems: 0,
      wellName: null,
      // Display names and metadata from cache
      columnDisplayNames: {
        fluidSampleID: 'Fluid Sample ID',
        field: 'Field',
        reservoir: 'Reservoir',
        fluidType: 'Fluid Type',
        fluidClassification: 'Fluid Classification',
        reservoirPressure: 'Reservoir Pressure',
        reservoirTemperature: 'Reservoir Temperature'
      },
      columnMetadata: {},
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalItems / this.itemsPerPage);
    },
    startIndex() {
      return (this.currentPage - 1) * this.itemsPerPage;
    },
    endIndex() {
      return Math.min(this.startIndex + this.itemsPerPage, this.totalItems);
    },
  },

  methods: {
    goBack() {
      this.$router.go(-1);
    },
    
    // Method to get display names for table columns from cache
    async loadColumnDisplayNames() {
      try {
        const columnKeys = [
          'fluidSampleID',
          'field', 
          'reservoir',
          'fluidType',
          'fluidClassification',
          'reservoirPressure',
          'reservoirTemperature'
        ];
        
        for (const key of columnKeys) {
          try {
            const result = await getDisplayName('sample', key);
            if (result && result.displayName) {
              this.columnDisplayNames[key] = result.displayName;
              this.columnMetadata[key] = {
                displayName: result.displayName,
                decimals: result.digits || 0
              };
            } else {
              // Fallback to formatted key name if result is invalid
              this.columnDisplayNames[key] = this.formatHeaderName(key);
              this.columnMetadata[key] = {
                displayName: this.formatHeaderName(key),
                decimals: 0
              };
            }
          } catch (error) {
            console.warn(`Could not get display name for ${key} from cache:`, error.message);
            // Fallback to formatted key name if not found in cache
            this.columnDisplayNames[key] = this.formatHeaderName(key);
            this.columnMetadata[key] = {
              displayName: this.formatHeaderName(key),
              decimals: 0
            };
          }
        }
        console.log('Column display names loaded:', this.columnDisplayNames);
      } catch (error) {
        console.error('Error loading column display names:', error);
        // Ensure we have fallback values
        const columnKeys = [
          'fluidSampleID',
          'field', 
          'reservoir',
          'fluidType',
          'fluidClassification',
          'reservoirPressure',
          'reservoirTemperature'
        ];
        
        columnKeys.forEach(key => {
          if (!this.columnDisplayNames[key]) {
            this.columnDisplayNames[key] = this.formatHeaderName(key);
            this.columnMetadata[key] = {
              displayName: this.formatHeaderName(key),
              decimals: 0
            };
          }
        });
      }
    },
    
    // Method to get UOM (Unit of Measure) for a property
    getUnitForProperty(key, dataSource) {
      // First try to get unit from the data source using _UOM suffix
      const uomKey = key + '_UOM';
      if (dataSource && dataSource[uomKey]) {
        return dataSource[uomKey];
      }
      
      // If no UOM field found, return empty string
      return "";
    },
    
    // Method to format header names as fallback
    formatHeaderName(key) {
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/_/g, ' ')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    },
    
    // Method to format values with proper decimals and UOM
    formatValueWithUOM(value, key, dataSource) {
      if (value === null || value === undefined || value === '-') {
        return value;
      }
      
      const metadata = this.columnMetadata?.[key];
      const decimals = metadata ? metadata.decimals : 0;
      const uom = this.getUnitForProperty(key, dataSource);
      
      // Format number with proper decimal places
      let formattedValue = value;
      if (typeof value === 'number' || !isNaN(parseFloat(value))) {
        formattedValue = parseFloat(value).toFixed(decimals);
      }
      
      // Add UOM if available
      if (uom) {
        return `${formattedValue} ${uom}`;
      }
      
      return formattedValue;
    },
    onTabChange() {
      this.currentPage = 1;
      this.fetchData();
    },
    onItemsPerPageChange() {
      this.currentPage = 1;
      this.fetchData();
    },
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.fetchData();
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.fetchData();
      }
    },
    async fetchData() {
      this.showLoader = true;
      this.timeoutMessage = "";
      
      try {
        // Load column display names from cache
        await this.loadColumnDisplayNames();
        
        if (this.wellName) {
          // Fetch real data from API
          await this.fetchWellSamples();
        } else {
          // No well name provided
          this.data = [];
          this.totalItems = 0;
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        this.timeoutMessage = "Failed to load data. Please try again.";
        this.data = [];
        this.totalItems = 0;
      } finally {
      this.showLoader = false;
      }
    },
    async fetchWellSamples() {
      try {
        const response = await api.get(`/public/api/samples?well="${this.wellName}"&viewSummary=true`);
        
        console.log('API Response:', response.data); // Debug log
        
        if (response.data && response.data.data && response.data.data.samples && response.data.data.samples.length > 0) {
          this.mapApiDataToTable(response.data.data.samples);
          // Update pagination info if available
          if (response.data.data.pagination) {
            this.totalItems = response.data.data.pagination.TotalRecords || response.data.data.samples.length;
          }
        } else {
          // No data found for this well
          this.data = [];
          this.totalItems = 0;
        }
      } catch (error) {
        console.error('API Error:', error);
        throw error;
      }
    },
    mapApiDataToTable(apiData) {
      console.log('Mapping API data:', apiData); // Debug log
      const mappedData = [];
      
      apiData.forEach((sample, index) => {
        // Extract test information from compositiontests
        const testsArray = this.extractTestsFromSample(sample);
        
        // Map the API response to table structure
        const mappedItem = {
          id: sample.ID || index + 1,
          fluidSampleID: sample.fluidSampleID || sample.uniqueID || `SMP-${index + 1}`,
          testsArray: testsArray,
          field: sample.location?.field || '-',
          reservoir: sample.location?.reservoir || '-',
          fluidType: sample.fluidType || '-',
          fluidClassification: this.getFluidClassification(sample),
          reservoirPressure: this.getReservoirPressure(sample),
          testTemperature: this.getTestTemperature(sample),
          reservoirTemperature: this.getReservoirTemperature(sample),
          tTestSelected: false,
          tResSelected: false,
          qcStatus: this.getQCStatus(sample),
          // Store original sample data for reference
          originalSample: sample
        };
        
        mappedData.push(mappedItem);
      });
      
      // Don't override totalItems here as it's set from pagination data
      const start = this.startIndex;
      const end = start + this.itemsPerPage;
      this.data = mappedData.slice(start, end);
      
      console.log('Mapped data:', this.data); // Debug log
      console.log('Total items:', this.totalItems); // Debug log
    },
    extractTestsFromSample(sample) {
      const tests = [];
      
      // Check for composition tests
      if (sample.compositiontests && sample.compositiontests.length > 0) {
        sample.compositiontests.forEach((test, index) => {
          tests.push({
            type: 'Composition',
            testType: 'compositiontests',
            testId: test.ID || test._id,
            testData: test,
            displayName: this.getCompositionDisplayName(test, sample.compositiontests.length, index),
            index: index
          });
        });
      }
      
      // Check for CCE tests
      if (sample.ccetests && sample.ccetests.length > 0) {
        sample.ccetests.forEach((test, index) => {
          tests.push({
            type: 'CCE Test',
            testType: 'ccetests',
            testId: test.ID || test._id,
            testData: test,
            displayName: this.getTestDisplayName(test, 'CCE', sample.ccetests.length, index),
            index: index
          });
        });
      }
      
      // Check for viscosity tests
      if (sample.viscositytests && sample.viscositytests.length > 0) {
        sample.viscositytests.forEach((test, index) => {
          tests.push({
            type: 'Viscosity Test',
            testType: 'viscositytests',
            testId: test.ID || test._id,
            testData: test,
            displayName: this.getTestDisplayName(test, 'Viscosity Test', sample.viscositytests.length, index),
            index: index
          });
        });
      }
      
      // Check for DL tests
      if (sample.dltests && sample.dltests.length > 0) {
        sample.dltests.forEach((test, index) => {
          tests.push({
            type: 'DL Test',
            testType: 'dltests',
            testId: test.ID || test._id,
            testData: test,
            displayName: this.getTestDisplayName(test, 'DL', sample.dltests.length, index),
            index: index
          });
        });
      }
      
      // Check for CVD tests
      if (sample.cvdtests && sample.cvdtests.length > 0) {
        sample.cvdtests.forEach((test, index) => {
          tests.push({
            type: 'CVD Test',
            testType: 'cvdtests',
            testId: test.ID || test._id,
            testData: test,
            displayName: this.getTestDisplayName(test, 'CVD', sample.cvdtests.length, index),
            index: index
          });
        });
      }
      
      // Check for separator tests
      if (sample.separatortests && sample.separatortests.length > 0) {
        sample.separatortests.forEach((test, index) => {
          tests.push({
            type: 'Separator Test',
            testType: 'separatortests',
            testId: test.ID || test._id,
            testData: test,
            displayName: this.getTestDisplayName(test, 'Separator Test', sample.separatortests.length, index),
            index: index
          });
        });
      }
      
      // Check for sample validation
      if (sample.IsTestAvailable) {
        tests.push({
          type: 'Sample Validation',
          testType: 'sampleValidation',
          testId: sample.ID || sample._id,
          testData: sample,
          displayName: 'Sample Validation',
          index: 0
        });
      }
      
      // If no tests found, show placeholder
      if (tests.length === 0) {
        tests.push({
          type: 'Sample Validation',
          testType: 'sampleValidation',
          testId: sample.ID || sample._id,
          testData: sample,
          displayName: 'Sample Validation',
          index: 0
        });
      }
      
      return tests;
    },
    
    // Helper method to get display name for composition tests
    getCompositionDisplayName(test, totalCount, index) {
      let displayName = 'Composition';
      
      if (test.contaminationType || test.recombinationType) {
        displayName += ' (';
        if (test.contaminationType) {
          displayName += test.contaminationType;
        } else if (test.recombinationType) {
          displayName += test.recombinationType;
        }
        displayName += ')';
      }
      
      // Add test number if multiple tests
      if (totalCount > 1) {
        displayName += ` (Test #${index + 1})`;
      }
      
      return displayName;
    },
    
    // Helper method to get display name for other tests with temperature
    getTestDisplayName(test, testType, totalCount, index) {
      let displayName = test.name || testType;
      
      // Add temperature if available (always show temperature regardless of test count)
      if (test.testTemperature) {
        const uom = test.testTemperature_UOM || '';
        displayName += ` (${test.testTemperature}${uom ? ` ${uom}` : ''})`;
      } else if (totalCount > 1) {
        // Only add test number if no temperature and multiple tests
        displayName += ` (Test #${index + 1})`;
      }
      
      return displayName;
    },
    
    getFluidClassification(sample) {
      // Determine fluid classification based on API data
      if (sample.fluidType === 'Oil') {
        return 'Oil';
      } else if (sample.fluidType === 'Gas') {
        return 'Gas';
      } else if (sample.fluidType) {
        return sample.fluidType;
      }
      return '-';
    },
    getReservoirPressure(sample) {
      // Check for other pressure fields
      if (sample.sampleProperties?.reservoirPressure) {
        return sample.sampleProperties.reservoirPressure;
      }
      return '-';
    },
    getTestTemperature(sample) {
      // Extract temperature information if available
      return '250'; // Default value as per mock data
    },
    getReservoirTemperature(sample) {
      // Extract reservoir temperature if available
      if (sample.reservoirTemperature) {
        return sample.reservoirTemperature;
      }
      // Check for temperature in sample properties
      if (sample.sampleProperties?.reservoirTemperature) {
        return sample.sampleProperties.reservoirTemperature;
      }
      if (sample.standardTemperature) {
        return sample.standardTemperature;
      }
      return '-';
    },
    getQCStatus(sample) {
      // Determine QC status based on sample data
      if (sample.compositiontests && sample.compositiontests.length > 0) {
        return 'Passed';
      }
      return 'Pending';
    },
     
     handleTestClick(test, item) {
       console.log('Test clicked:', test, 'for item:', item.fluidSampleID);
       console.log('Original sample data:', item.originalSample);
       
       // Store test and sample information for the unified test view
       // Use _id for sampleId as per API requirements
       const sampleId = item.originalSample?._id || item.originalSample?.ID || item.id;
       const testId = test.testId;
       
       localStorage.setItem('testId', testId);
       localStorage.setItem('sampleId', sampleId);
       
       console.log('Using sampleId:', sampleId, 'testId:', testId);
       
       // Map test types to API endpoints
       const testTypeMap = {
         'CCE Test': 'ccetest',
         'DL Test': 'dltest', 
         'CVD Test': 'cvdtest',
         'Viscosity Test': 'viscositytest',
         'Separator Test': 'separatortest',
         'Composition': 'compositiontest',
         'Sample Validation': 'samplevalidation'
       };
       
       const testType = testTypeMap[test.type] || 'ccetest';
       
       // Navigate to unified test view
       this.$router.push({
         name: 'UnifiedTestView',
         params: { 
           testType: testType,
           testId: testId
         },
       });
     },
     
    getQCStatusColor(status) {
      const statusColors = {
        'Passed': 'success',
        'Pending': 'warning'
      };
      return statusColors[status] || 'grey';
    },
    getTestStatusClass(test, item) {
      // Check the actual IsVerified status from the API data
      const sample = item.originalSample;
      if (!sample) {
        return 'test-status-pending clickable-test';
      }
      
      // Handle Sample Validation separately as it's not in a test array
      if (test.type === 'Sample Validation') {
        // For sample validation, check if IsTestAvailable is true
        if (sample.IsTestAvailable) {
          return 'test-status-passed clickable-test';
        } else {
          return 'test-status-pending clickable-test';
        }
      }
      
      // For other tests, check the IsVerified status in the test data
      if (test.testData && test.testData.hasOwnProperty('IsVerified')) {
        const isVerifiedValue = test.testData.IsVerified;
        
        if (isVerifiedValue === true || isVerifiedValue === 1 || isVerifiedValue === '1') {
          return 'test-status-passed clickable-test';
        } else if (isVerifiedValue === false || isVerifiedValue === 0 || isVerifiedValue === '0') {
          return 'test-status-failed clickable-test';
        } else {
          return 'test-status-pending clickable-test';
        }
      }
      
      // Default to pending if no test data found
      return 'test-status-pending clickable-test';
    }
  },
  watch: {
    // Watch for route changes to handle navigation to different wells
    '$route'(to, from) {
      if (to.query.well !== from.query.well) {
        this.wellName = to.query.well || null;
        this.currentPage = 1; // Reset to first page
        if (this.wellName) {
          this.fetchData();
        } else {
          // If no well name, show empty state
          this.data = [];
          this.totalItems = 0;
        }
      }
    }
  },
  mounted() {
    // Get well name from query parameters
    this.wellName = this.$route.query.well || null;
    console.log('DetailView mounted - Well name:', this.wellName); // Debug log
    console.log('Current route:', this.$route); // Debug log
    console.log('Query parameters:', this.$route.query); // Debug log
    console.log('Full URL:', window.location.href); // Debug log
    
    // Check if well name exists in localStorage as fallback
    if (!this.wellName) {
      const storedWellName = localStorage.getItem('lastSelectedWell');
      if (storedWellName) {
        console.log('Using stored well name:', storedWellName);
        this.wellName = storedWellName;
        // Update URL to include the well name
        this.$router.replace({
          name: 'QAQCDetailView',
          query: { ...this.$route.query, well: storedWellName }
        });
      }
    }
    
    if (this.wellName) {
      // Store the well name for future use
      localStorage.setItem('lastSelectedWell', this.wellName);
      this.fetchData();
    } else {
      // If no well name in query, show empty state instead of redirecting
      console.log('No well name found, showing empty state');
      this.data = [];
      this.totalItems = 0;
    }
  },
};
</script>

<style scoped>
@import '../../assets/Styling/qaqc.css';

/* Override existing styles for better integration */
.detail-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.back-btn {
  margin-right: 16px;
}

.qa-tabs {
  margin-top: 16px;
  margin-bottom: 16px;
}

.qa-tabs .v-tab {
  font-size: 16px;
  font-weight: 500;
  text-transform: none;
  min-height: 30px;
}

.qa-tabs .v-tab--selected {
  font-weight: 600;
}

.cursor-pointer {
  cursor: pointer;
}

.clickable-cell:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.no-asset-div {
  padding: 48px 16px;
  text-align: center;
}

.no-asset-div h4 {
  margin: 16px 0 8px 0;
  color: #666;
}

.no-asset-div p {
  color: #999;
  margin: 0;
}

.timeout {
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 4px;
}

/* Ensure proper table containment */
.qaqc-main-container {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.qaqc-table-section {
  width: 100%;
  overflow-x: auto;
}

/* Remove any extra spacing or background that might cause blue highlight */
.qaqc-main-table .v-table__wrapper {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  margin: 0 !important;
  padding: 0 !important;
  height: auto !important;
  min-height: 0 !important;
}

.qaqc-main-table .v-table {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  margin: 0 !important;
  padding: 0 !important;
}

.qaqc-main-table thead {
  background: transparent !important;
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
  height: auto !important;
  min-height: 0 !important;
}

.qaqc-main-table thead tr {
  background: transparent !important;
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
  height: auto !important;
  min-height: 0 !important;
}

/* Ensure no extra spacing above table header */
.qaqc-main-table thead tr:first-child {
  margin-top: 0 !important;
  padding-top: 0 !important;
  border: none !important;
  height: auto !important;
  min-height: 0 !important;
}

.qaqc-main-table thead tr:first-child th {
  margin-top: 0 !important;
  padding-top: 16px !important;
  border: none !important;
  background: #2298D5 !important;
}

/* Target Vuetify specific classes that might be causing the blue space */
.v-table .v-table__wrapper {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  margin: 0 !important;
  padding: 0 !important;
}

.v-table thead {
  background: transparent !important;
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
}

.v-table thead tr {
  background: transparent !important;
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
}

/* Fix table width issues */
.qaqc-main-table {
  width: 100%;
  min-width: 800px;
}

/* Additional overrides to remove blue space */
.qaqc-table-section .v-table {
  background: transparent !important;
}

.qaqc-table-section .v-table .v-table__wrapper {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
}

.qaqc-table-section .v-table thead {
  background: transparent !important;
}

.qaqc-table-section .v-table thead tr {
  background: transparent !important;
}

/* Force remove any default Vuetify table spacing */
.data-management .v-table {
  background: transparent !important;
}

.data-management .v-table .v-table__wrapper {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  margin: 0 !important;
  padding: 0 !important;
}

/* Ensure proper text wrapping in cells */
.qaqc-main-table td {
  word-wrap: break-word;
  word-break: break-word;
  white-space: normal;
}

/* Specific column widths for better layout */
.qaqc-main-table th:nth-child(1),
.qaqc-main-table td:nth-child(1) {
  width: 120px;
  min-width: 120px;
}



.qaqc-main-table th:nth-child(3),
.qaqc-main-table td:nth-child(3),
.qaqc-main-table th:nth-child(4),
.qaqc-main-table td:nth-child(4),
.qaqc-main-table th:nth-child(5),
.qaqc-main-table td:nth-child(5),
.qaqc-main-table th:nth-child(6),
.qaqc-main-table td:nth-child(6) {
  width: 100px;
  min-width: 100px;
}

.qaqc-main-table th:nth-child(7),
.qaqc-main-table td:nth-child(7),
.qaqc-main-table th:nth-child(8),
.qaqc-main-table td:nth-child(8) {
  width: 120px;
  min-width: 120px;
}

/* Multiple tests display styling */
.qaqc-tests-container {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}

.qaqc-tests-container span {
  display: inline-block;
  padding: 4px 8px;
  margin: 2px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  text-align: center;
  min-width: fit-content;
}

.qaqc-tests-container span:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Test status styling */
.test-status-passed {
  background-color: #e8f5e8;
  color: #2e7d32;
}

.test-status-failed {
  background-color: #ffebee;
  color: #c62828;
}

.test-status-pending {
  background-color: #fff3e0;
  color: #313130;
}

.clickable-test {
  cursor: pointer;
}

.clickable-test:hover {
  opacity: 0.8;
}

/* Ensure proper spacing for test column */
.qaqc-main-table td:nth-child(2) {
  min-width: 200px;
  max-width: 300px;
  word-wrap: break-word;
}
</style>
